import React, { memo } from 'react';
import { connect } from 'react-redux';
import { selectors as groupSelectors } from '../../../../state/modules/groups';

import { Badge } from '../../../ui-kit';
import SettingsForm from '../ui-kit/SettingsForm';
import colors from '../../../../utils/colors';

export const WARNING_LEVEL_SHOW = 'show';
export const WARNING_LEVEL_HIDE = 'hide';
export const WARNING_LEVEL_EXPAND = 'expand';

const warningLevelDisplayOptions = () => [
  { value: WARNING_LEVEL_EXPAND, name: I18n.t('expand') },
  { value: WARNING_LEVEL_SHOW, name: I18n.t('show') },
  { value: WARNING_LEVEL_HIDE, name: I18n.t('hide') }
];

// migrate the settings from the old format to the new one. Before, the settings
// were boolean values. Now, they are strings ('show', 'hide', 'expand')
export const upgradeWarningLevelSettings = ({
  show_above_buffer,
  show_on_buffer,
  show_on_required,
  show_below_required
}) => {
  const isBoolean = value => typeof value === 'boolean';

  const upgradeWarningLevelSetting = (value, understaffed) => {
    if (isBoolean(value) && understaffed) return value ? WARNING_LEVEL_EXPAND : WARNING_LEVEL_HIDE;
    else if (isBoolean(value)) return value ? WARNING_LEVEL_SHOW : WARNING_LEVEL_HIDE;
    else return value;
  };

  return {
    show_above_buffer: upgradeWarningLevelSetting(show_above_buffer),
    show_on_buffer: upgradeWarningLevelSetting(show_on_buffer),
    show_below_buffer: upgradeWarningLevelSetting(show_on_buffer),
    show_on_required: upgradeWarningLevelSetting(show_on_required),
    show_below_required: upgradeWarningLevelSetting(show_below_required, true)
  };
};

const warningLevels = ['show_above_buffer', 'show_on_buffer', 'show_on_required', 'show_below_required'];

const VisibleWarningLevelsSelector = memo(() => (
  <>
    {warningLevels.map(level => (
      <SettingsForm.RadioButtonGroup
        fieldName={level}
        key={level}
        options={warningLevelDisplayOptions()}
        label={
          <h5>
            <Badge color={colors[level.slice(5)]}>
              {I18n.t('classy_enum.warnings/warning_level.' + level.slice(5))}
            </Badge>
          </h5>
        }
      />
    ))}
  </>
));
VisibleWarningLevelsSelector.displayName = 'VisibleWarningLevelsSelector';

const Settings = ({
  groupsById,
  groupId,
  groupsTree,
  updateWidgetSettingsField,
  accessScopeGroupId,
  widgetSettings
}) => {
  const currentGroup = groupsById[groupId];

  const { visible_availability_requirements } = widgetSettings;

  const visibleAvailabilityRequirements =
    visible_availability_requirements || currentGroup?.availability_requirements?.map(ar => ar.id) || [];

  const handleAvailabilityRequirementChange = ({ selectedAvailabilityRequirements }) => {
    updateWidgetSettingsField('visible_availability_requirements', selectedAvailabilityRequirements);
  };

  // extract settings related to showing a particular level
  // this approach reduces code repetition
  const warningLevels = upgradeWarningLevelSettings(widgetSettings);
  widgetSettings = { ...widgetSettings, ...warningLevels };

  return (
    <>
      <SettingsForm
        withAutoScroll={true}
        withAutoResizeHeight={true}
        updateWidgetSettingsField={updateWidgetSettingsField}
        widgetSettings={widgetSettings}
      >
        <SettingsForm.Section>
          <SettingsForm.Section.Header>{I18n.t('display')}</SettingsForm.Section.Header>
          <SettingsForm.Section.Content>
            <SettingsForm.ToggleField
              fieldName="show_all_requirements"
              label={I18n.t('dashboards.widget_archetypes.crew_level_warnings_v2.settings.hidden')}
            />
            <SettingsForm.ToggleField
              fieldName="show_names_when_orange_or_red"
              label={I18n.t(
                'dashboards.widget_archetypes.crew_level_warnings_v2.settings.show_names_when_orange_or_red'
              )}
            />
            <SettingsForm.AvailabilityRequirementsSelector
              groupsTree={groupsTree}
              parentGroupId={accessScopeGroupId || groupId}
              selectedAvailabilityRequirements={visibleAvailabilityRequirements}
              onChange={handleAvailabilityRequirementChange}
            />
          </SettingsForm.Section.Content>
        </SettingsForm.Section>
        <SettingsForm.Section>
          <SettingsForm.Section.Header>{I18n.t('availability.availability_level')}</SettingsForm.Section.Header>
          <SettingsForm.Section.Content>
            <VisibleWarningLevelsSelector />
          </SettingsForm.Section.Content>
        </SettingsForm.Section>
      </SettingsForm>
    </>
  );
};

const mapStateToProps = state => ({
  groupsById: groupSelectors.byId(state),
  groupsTree: groupSelectors.tree(state)
});

export default connect(mapStateToProps)(Settings);
